/**
 * accountGroupDetail.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";

import { FormControl, FormLabel, Radio, RadioGroup, TextField } from "@mui/material"


import { Group, GroupType } from "../models/group";

interface Props {
  group: Group,
  updateGroupName(accountID: string, groupID: string, groupname: string): Promise<Group | null>,
  updateGroup(accountID: string, groupID: string, settings: Partial<Group>): Promise<Group | null>
}
export function AccountGroupDetailComponent(props: Props): React.ReactElement<any, any> {
  const [groupname, setGroupName] = React.useState<string>(props.group.groupname);
  const [groupDesc, setGroupDesc] = React.useState<string>(props.group.desc);
  const [grouptype, setGroupType] = React.useState<GroupType>(props.group.grouptype);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const descRef = React.useRef<HTMLInputElement | null>(null);

  function updateValue(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = inputElem.value;
    const update: Partial<Group> = {};
    switch (propName) {
      case "groupname":
        setGroupName(newValue);
        update.groupname = newValue;
        break;
      case "desc":
        setGroupDesc(newValue);
        update.desc = newValue;
        break;
      default:
        return;
    }

    timer && clearTimeout(timer);
    setTimer(setTimeout(async () => {
      setTimer(null);
      switch (propName) {
        case "groupname":
          await props.updateGroupName(props.group.accountID, props.group._id, newValue);
          break;
        default:
          await props.updateGroup(props.group.accountID, props.group._id, update);
          break;
      }
      
    }, 1000));
  }

  function onKeyUp(event: React.KeyboardEvent): void {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "groupname") {
      descRef.current?.focus();
    } else if (propName === "desc") {
      descRef.current?.blur();
    }
  }

  async function updateGroupType(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = event.target.value as GroupType;
    console.log("new value:", newValue);
    setGroupType(newValue);
    const g = await props.updateGroup(props.group.accountID, props.group._id, {grouptype: newValue});
    if (g) {
      setGroupType(g.grouptype);
    }
  }

  return <>
    <FormControl fullWidth className="form-row">
      <TextField label="Group Name" variant="outlined" fullWidth name="groupname" required
        size="small" value={groupname}
        onChange={updateValue}
        onKeyUp={onKeyUp} />
    </FormControl>
    <FormControl fullWidth className="form-row">
    <TextField className="group-desc" id="desc" name="desc" fullWidth multiline
          variant="outlined" label="Group Description" size="small"
          inputRef={descRef}
          value={groupDesc} onChange={updateValue}  />
    </FormControl>
    <div className="form-row">
      <FormControl component="fieldset" className="group-type-selection">
        <FormLabel component="legend">Visibility &amp; Access</FormLabel>
        <RadioGroup name="grouptype" color="secondary"
            value={grouptype}
            onChange={updateGroupType}>
          <FormLabel>
            <Radio value={GroupType.public} name="grouptype" disableRipple />
            <div className="radio-label-desc">
              <dt>Open</dt>
              <dd>Visible to members of this account. Members can join on their own.
                Guests must be added.
              </dd>
            </div>
          </FormLabel>
          <FormLabel>
            <Radio value={GroupType.private} name="grouptype" disableRipple />
            <div className="radio-label-desc">
              <dt>Private</dt>
              <dd>Not shown to members of this account. Invite only.</dd>
            </div>
          </FormLabel>
        </RadioGroup>
      </FormControl>
      </div>
  </>;
}
